import React, { useMemo, useState, useEffect, useRef } from 'react'
import { getCountryCodeFromClient, detectCountryFromPhone } from '../../../pure-js/libs/PhoneHelper'
import { Client } from '../../../pure-js/types/GrooverTypes'
import { TextFieldWithLabelForm } from './TextFieldWithLabelForm'
import { CountrySelector } from './CountrySelector'
import { getAllCountries } from '../../../pure-js/libs/CountryHelper'
import { getLanguageCode } from '../hooks/useDefaultLanguage'
import styled from '@emotion/styled'

// Import translation files
import enTranslations from '../../../pure-js/assets/i18n/en.json'
import svTranslations from '../../../pure-js/assets/i18n/sv.json'
import noTranslations from '../../../pure-js/assets/i18n/no.json'

const translations: Record<string, Record<string, string>> = {
  en: enTranslations,
  sv: svTranslations,
  no: noTranslations
}

// Country code to flag emoji mapping - EU countries
const COUNTRY_FLAGS: Record<string, string> = {
  // EU Member States
  AT: '🇦🇹', // Austria
  BE: '🇧🇪', // Belgium
  BG: '🇧🇬', // Bulgaria
  HR: '🇭🇷', // Croatia
  CY: '🇨🇾', // Cyprus
  CZ: '🇨🇿', // Czech Republic
  DK: '🇩🇰', // Denmark
  EE: '🇪🇪', // Estonia
  FI: '🇫🇮', // Finland
  FR: '🇫🇷', // France
  DE: '🇩🇪', // Germany
  GR: '🇬🇷', // Greece
  HU: '🇭🇺', // Hungary
  IE: '🇮🇪', // Ireland
  IT: '🇮🇹', // Italy
  LV: '🇱🇻', // Latvia
  LT: '🇱🇹', // Lithuania
  LU: '🇱🇺', // Luxembourg
  MT: '🇲🇹', // Malta
  NL: '🇳🇱', // Netherlands
  PL: '🇵🇱', // Poland
  PT: '🇵🇹', // Portugal
  RO: '🇷🇴', // Romania
  SK: '🇸🇰', // Slovakia
  SI: '🇸🇮', // Slovenia
  ES: '🇪🇸', // Spain
  SE: '🇸🇪', // Sweden

  // EEA countries (not EU but part of European Economic Area)
  IS: '🇮🇸', // Iceland
  LI: '🇱🇮', // Liechtenstein
  NO: '🇳🇴', // Norway

  // Other European countries
  CH: '🇨🇭', // Switzerland
  GB: '🇬🇧', // United Kingdom
  AL: '🇦🇱', // Albania
  AD: '🇦🇩', // Andorra
  BY: '🇧🇾', // Belarus
  BA: '🇧🇦', // Bosnia and Herzegovina
  MK: '🇲🇰', // North Macedonia
  MD: '🇲🇩', // Moldova
  MC: '🇲🇨', // Monaco
  ME: '🇲🇪', // Montenegro
  RS: '🇷🇸', // Serbia
  SM: '🇸🇲', // San Marino
  UA: '🇺🇦', // Ukraine
  VA: '🇻🇦', // Vatican City

  // Other commonly used countries
  US: '🇺🇸', // United States
  CA: '🇨🇦', // Canada
  AU: '🇦🇺', // Australia
  NZ: '🇳🇿', // New Zealand
  KR: '🇰🇷' // South Korea
}

type PhoneInputWithFlagProps = {
  client?: Client | null // Client object to get country from
  defaultCountry?: string // Country code (e.g., 'SE', 'NO') - overrides client.country if provided
  disabled?: boolean
  placeholder?: string
  autoComplete?: string
  grooverLabelTextKey?: string
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  fieldState?: any
  fullWidth?: boolean
  name?: string // Required by TextFieldWithLabelForm
  ErrorComponent?: React.FC<any>
  onCountryChange?: (countryCode: string) => void // Callback when country changes
}

export const PhoneInputWithFlag: React.FC<PhoneInputWithFlagProps> = ({
  client,
  defaultCountry,
  disabled,
  placeholder,
  autoComplete = 'tel',
  grooverLabelTextKey,
  value = '',
  onChange,
  fieldState,
  fullWidth,
  name = 'phoneNumber',
  ErrorComponent,
  onCountryChange
}) => {
  // Determine default country from client or provided defaultCountry
  const defaultCountryCode = useMemo(() => {
    if (defaultCountry) {
      return defaultCountry.toUpperCase() as any
    }
    return getCountryCodeFromClient(client)
  }, [client, defaultCountry])

  // State for selected country (initialized from first defaultCountryCode only — see sync effect below)
  const [selectedCountry, setSelectedCountry] = useState<string>(defaultCountryCode)

  /** True after user picks from dropdown or phone auto-detect changes country — skip syncing from client default. */
  const userOverrodeCountryRef = useRef(false)

  // When client loads async, defaultCountryCode can change (e.g. undefined→NO) while useState still holds the first render (SE).
  useEffect(() => {
    if (userOverrodeCountryRef.current) return
    setSelectedCountry(defaultCountryCode)
  }, [defaultCountryCode])

  // Get current language
  const language = getLanguageCode()

  // Translation function
  const translate = useMemo(() => {
    const langTranslations = translations[language] || translations.en
    return (key: string) => langTranslations[key] || key
  }, [language])

  // Get all countries metadata with translations
  const countries = useMemo(() => getAllCountries(COUNTRY_FLAGS, translate), [translate])

  // Auto-detect country from phone number
  useEffect(() => {
    if (!value) return
    const detected = detectCountryFromPhone(value)
    if (detected && detected !== selectedCountry) {
      userOverrodeCountryRef.current = true
      setSelectedCountry(detected)
      if (onCountryChange) {
        onCountryChange(detected)
      }
    }
  }, [value, selectedCountry, onCountryChange])

  // Handle country selection
  const handleCountryChange = (event: any) => {
    const newCountry = event.target.value
    userOverrodeCountryRef.current = true
    setSelectedCountry(newCountry)
    if (onCountryChange) {
      onCountryChange(newCountry)
    }
  }

  return (
    <PhoneInputWrapper>
      <CountrySelector
        value={selectedCountry}
        onChange={handleCountryChange}
        countries={countries}
        disabled={disabled}
      />
      <TextFieldWithLabelForm
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        fullWidth={fullWidth}
        fieldState={fieldState}
        placeholder={placeholder}
        autoComplete={autoComplete}
        grooverLabelTextKey={grooverLabelTextKey}
        ErrorComponent={ErrorComponent}
      />
    </PhoneInputWrapper>
  )
}

const PhoneInputWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
`
